import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';

import ConfirmableButton from '../../../components/ConfirmableButton';
import { useToast } from '../../../context/ToastContext';
import { useUser } from '../../../hooks/useUser';
import { useDatevAccountNumber } from '../hooks/useDatevAccountNumber';

const DatevAccountNumber: React.FC = () => {
  const {
    accountNumber,
    loading,
    creating,
    updating,
    deleting,
    error,
    create,
    update,
    delete: deleteAccount,
  } = useDatevAccountNumber();
  const {
    user,
    loading: userLoading,
    updating: userUpdating,
    updateUser,
  } = useUser();
  const { showToast } = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    accountNumber: '',
    description: '',
    beraternummer: '',
    mandantennummer: '',
  });

  // Show error from React Query as toast
  useEffect(() => {
    if (error) {
      showToast('Failed to load Datev account number', error, 'error');
    }
  }, [error, showToast]);

  // Reset form when account number or user changes
  useEffect(() => {
    if (!isEditing) {
      setFormData({
        accountNumber: accountNumber?.accountNumber || '',
        description: accountNumber?.description || '',
        beraternummer: user?.datevBeraternummer || '',
        mandantennummer: user?.datevMandantennummer || '',
      });
    }
  }, [accountNumber, user, isEditing]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.accountNumber.trim()) {
      showToast('Validation error', 'Account number is required', 'error');
      return;
    }

    try {
      // Save account number
      await create({
        accountNumber: formData.accountNumber.trim(),
        description: formData.description.trim() || undefined,
      });

      // Save user fields (beraternummer and mandantennummer)
      await updateUser({
        datevBeraternummer: formData.beraternummer.trim() || undefined,
        datevMandantennummer: formData.mandantennummer.trim() || undefined,
      });

      showToast(
        'Datev account number added',
        'The account number has been saved successfully.',
        'success'
      );
      setFormData({
        accountNumber: '',
        description: '',
        beraternummer: '',
        mandantennummer: '',
      });
    } catch (err) {
      showToast(
        'Failed to add account number',
        err instanceof Error ? err.message : 'An unexpected error occurred',
        'error'
      );
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!accountNumber) return;

    if (!formData.accountNumber.trim()) {
      showToast('Validation error', 'Account number is required', 'error');
      return;
    }

    try {
      // Update account number
      await update({
        id: accountNumber.id,
        data: {
          accountNumber: formData.accountNumber.trim(),
          description: formData.description.trim() || undefined,
        },
      });

      // Update user fields (beraternummer and mandantennummer)
      await updateUser({
        datevBeraternummer: formData.beraternummer.trim() || undefined,
        datevMandantennummer: formData.mandantennummer.trim() || undefined,
      });

      showToast(
        'Datev account number updated',
        'The account number has been updated successfully.',
        'success'
      );
      setIsEditing(false);
    } catch (err) {
      showToast(
        'Failed to update account number',
        err instanceof Error ? err.message : 'An unexpected error occurred',
        'error'
      );
    }
  };

  const handleDelete = async () => {
    if (!accountNumber) return;

    try {
      await deleteAccount(accountNumber.id);
      showToast(
        'Datev account number deleted',
        'The account number has been removed successfully.',
        'success'
      );
      setFormData({
        accountNumber: '',
        description: '',
        beraternummer: user?.datevBeraternummer || '',
        mandantennummer: user?.datevMandantennummer || '',
      });
    } catch (err) {
      showToast(
        'Failed to delete account number',
        err instanceof Error ? err.message : 'An unexpected error occurred',
        'error'
      );
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFormData({
      accountNumber: accountNumber?.accountNumber || '',
      description: accountNumber?.description || '',
      beraternummer: user?.datevBeraternummer || '',
      mandantennummer: user?.datevMandantennummer || '',
    });
  };

  if (loading || userLoading) {
    return (
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
        <div className='animate-pulse'>
          <div className='h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4'></div>
          <div className='h-20 bg-gray-200 dark:bg-gray-700 rounded'></div>
        </div>
      </div>
    );
  }

  // Hide when booking system is not DATEV
  if (user?.bookingSystem !== 'datev' && user?.bookingSystem !== undefined) {
    return null;
  }

  // If no account number exists, show inline form
  if (!accountNumber) {
    return (
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
        <h2 className='text-xl font-semibold mb-4 text-gray-900 dark:text-white'>
          Datev Account Number
        </h2>

        <form onSubmit={handleCreate} className='space-y-4'>
          <div>
            <label
              htmlFor='account-number-input'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              Account Number *
            </label>
            <input
              id='account-number-input'
              type='text'
              value={formData.accountNumber}
              onChange={e =>
                setFormData(prev => ({ ...prev, accountNumber: e.target.value }))
              }
              placeholder='Enter Datev account number'
              required
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          <div>
            <label
              htmlFor='description-input'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              Description (optional)
            </label>
            <input
              id='description-input'
              type='text'
              value={formData.description}
              onChange={e =>
                setFormData(prev => ({ ...prev, description: e.target.value }))
              }
              placeholder='Enter a description'
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          <div>
            <label
              htmlFor='beraternummer-input'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              Beraternummer (optional)
            </label>
            <input
              id='beraternummer-input'
              type='text'
              value={formData.beraternummer}
              onChange={e =>
                setFormData(prev => ({ ...prev, beraternummer: e.target.value }))
              }
              placeholder='Enter Beraternummer'
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          <div>
            <label
              htmlFor='mandantennummer-input'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              Mandantennummer (optional)
            </label>
            <input
              id='mandantennummer-input'
              type='text'
              value={formData.mandantennummer}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  mandantennummer: e.target.value,
                }))
              }
              placeholder='Enter Mandantennummer'
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          <div className='flex justify-end'>
            <button
              type='submit'
              disabled={creating || userUpdating || !formData.accountNumber.trim()}
              className='px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
            >
              {creating || userUpdating ? 'Saving...' : 'Save Account Number'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // If editing, show edit form
  if (isEditing) {
    return (
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
        <div className='flex justify-between items-start mb-4 min-h-[40px]'>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
            Datev Account Number
          </h2>
        </div>

        <form onSubmit={handleUpdate} className='space-y-3'>
          <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
            <label
              htmlFor='edit-account-number-input'
              className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'
            >
              Account Number
            </label>
            <input
              id='edit-account-number-input'
              type='text'
              value={formData.accountNumber}
              onChange={e =>
                setFormData(prev => ({ ...prev, accountNumber: e.target.value }))
              }
              placeholder='Enter Datev account number'
              required
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
            <label
              htmlFor='edit-description-input'
              className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'
            >
              Description
            </label>
            <input
              id='edit-description-input'
              type='text'
              value={formData.description}
              onChange={e =>
                setFormData(prev => ({ ...prev, description: e.target.value }))
              }
              placeholder='Enter a description'
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
            <label
              htmlFor='edit-beraternummer-input'
              className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'
            >
              Beraternummer
            </label>
            <input
              id='edit-beraternummer-input'
              type='text'
              value={formData.beraternummer}
              onChange={e =>
                setFormData(prev => ({ ...prev, beraternummer: e.target.value }))
              }
              placeholder='Enter Beraternummer'
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
            <label
              htmlFor='edit-mandantennummer-input'
              className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'
            >
              Mandantennummer
            </label>
            <input
              id='edit-mandantennummer-input'
              type='text'
              value={formData.mandantennummer}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  mandantennummer: e.target.value,
                }))
              }
              placeholder='Enter Mandantennummer'
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          <div className='flex justify-end gap-2 pt-1'>
            <button
              type='button'
              onClick={handleCancelEdit}
              disabled={updating || userUpdating}
              className='px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={updating || userUpdating || !formData.accountNumber.trim()}
              className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
            >
              {updating || userUpdating ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Display mode: Show account number with edit/delete buttons
  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
      <div className='flex justify-between items-start mb-4 min-h-[40px]'>
        <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
          Datev Account Number
        </h2>
        <div className='flex gap-2 items-start min-h-[40px]'>
          <button
            onClick={() => setIsEditing(true)}
            className='p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-md transition-colors'
            aria-label='Edit account number'
            style={{ visibility: showDeleteConfirmation ? 'hidden' : 'visible' }}
          >
            <PencilIcon className='w-5 h-5' />
          </button>
          <ConfirmableButton
            onConfirm={handleDelete}
            onShowConfirmation={setShowDeleteConfirmation}
            buttonClassName='p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 rounded-md transition-colors'
            confirmButtonClassName='p-1 bg-white dark:bg-gray-800 rounded-full border border-gray-300 dark:border-gray-600 text-green-600 hover:text-green-800 hover:border-green-600'
            cancelButtonClassName='p-1 bg-white dark:bg-gray-800 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 hover:text-gray-800 hover:border-gray-600'
          >
            {deleting ? (
              <span className='text-sm px-1'>Deleting...</span>
            ) : (
              <TrashIcon className='w-5 h-5' />
            )}
          </ConfirmableButton>
        </div>
      </div>

      <div className='space-y-3'>
        <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
          <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
            Account Number
          </span>
          <p className='text-gray-900 dark:text-white font-mono'>
            {accountNumber.accountNumber}
          </p>
        </div>

        {accountNumber.description && (
          <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
            <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
              Description
            </span>
            <p className='text-gray-900 dark:text-white'>
              {accountNumber.description}
            </p>
          </div>
        )}

        <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
          <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
            Beraternummer
          </span>
          <p className='text-gray-900 dark:text-white font-mono'>
            {user?.datevBeraternummer || 'N/A'}
          </p>
        </div>

        <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
          <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
            Mandantennummer
          </span>
          <p className='text-gray-900 dark:text-white font-mono'>
            {user?.datevMandantennummer || 'N/A'}
          </p>
        </div>

        <div className='pb-3'>
          <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
            Added
          </span>
          <p className='text-sm text-gray-600 dark:text-gray-400'>
            {new Date(accountNumber.createdAt).toLocaleDateString()} at{' '}
            {new Date(accountNumber.createdAt).toLocaleTimeString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DatevAccountNumber;
